import { useCallback, useState } from 'react';

export enum Status {
  Idle = 'idle',
  Pending = 'pending',
  Resolved = 'resolved',
  Rejected = 'rejected',
}

type DataType<R> = R extends { data: infer T } ? T : R;

const useApi = <TParams extends any[], TActionResult>(
  operation: (...params: TParams) => Promise<TActionResult>
): [(...params: TParams) => Promise<any>, DataType<TActionResult> | undefined, Status, any, () => void] => {
  const initialState: {
    value?: DataType<TActionResult>;
    status: Status;
    error: any;
  } = { value: undefined, status: Status.Idle, error: null };
  const [result, setResult] = useState(initialState);

  const call = useCallback(
    async (...params: TParams) => {
      setResult((oldState) => ({ ...oldState, status: Status.Pending }));

      try {
        const response = await operation(...params);
        // @ts-ignore
        const data = response && 'data' in response ? (response as any).data : response;
        const resultValue = data || data === 0 ? data : {};

        setResult({ value: resultValue, status: Status.Resolved, error: null });
        return response;
      } catch (error) {
        setResult({ value: undefined, status: Status.Rejected, error });
        return null;
      }
    },
    [operation]
  );

  const handleResetState = () => setResult(initialState);

  return [call, result.value, result.status, result.error, handleResetState];
};

export default useApi;
